import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Layers, FolderOpen, Palette } from 'lucide-react';
import { ColorImage } from '../../admin/fabric/ColorImage';
import { tierLabels } from '../../../lib/fabricLabels';
import { brandLabel } from '../../../lib/brandNames';

// Brand → Collection → Material navigation tree for the public Swatch Gallery (mirrors the admin tree).
export const FabricTreeNav = ({ entries, scope, onScope }) => {
  const tree = useMemo(() => {
    const brands = new Map();
    entries.forEach((e) => {
      if (!brands.has(e.brand_name)) brands.set(e.brand_name, { name: e.brand_name, count: 0, collections: new Map() });
      const b = brands.get(e.brand_name);
      b.count++;
      if (!b.collections.has(e.collection_name)) b.collections.set(e.collection_name, { name: e.collection_name, count: 0, thumb: e.image_url, materials: new Map() });
      const c = b.collections.get(e.collection_name);
      c.count++;
      if (e.material_name) {
        if (!c.materials.has(e.material_name)) c.materials.set(e.material_name, { name: e.material_name, count: 0, thumb: e.image_url });
        c.materials.get(e.material_name).count++;
      }
    });
    return [...brands.values()].map((b) => ({ ...b, collections: [...b.collections.values()].map((c) => ({ ...c, materials: [...c.materials.values()] })) }));
  }, [entries]);

  const [openBrands, setOpenBrands] = useState(() => Object.fromEntries(tree.map((b) => [b.name, true])));
  const [openColls, setOpenColls] = useState({});

  const isBrand = (b) => scope.brand === b && !scope.collection;
  const isColl = (b, c) => scope.brand === b && scope.collection === c && !scope.material;
  const isMat = (b, c, m) => scope.brand === b && scope.collection === c && scope.material === m;

  return (
    <nav className="rounded-xl border border-neutral-200 bg-white overflow-hidden text-sm" data-testid="fabric-tree-nav">
      <button
        onClick={() => onScope({})}
        className={`w-full flex items-center gap-2 px-4 py-3 text-left font-medium border-b border-neutral-100 ${!scope.brand ? 'bg-amber-50 text-amber-800' : 'hover:bg-neutral-50 text-neutral-800'}`}
        data-testid="fabric-tree-all"
      >
        <Layers className="w-4 h-4" /> All fabrics <span className="ml-auto text-xs text-neutral-400">{entries.length}</span>
      </button>
      {tree.map((b) => (
        <div key={b.name} className="border-b border-neutral-100 last:border-b-0">
          <div className={`flex items-center gap-1 px-2 py-2 ${isBrand(b.name) ? 'bg-amber-50' : ''}`}>
            <button onClick={() => setOpenBrands((p) => ({ ...p, [b.name]: !p[b.name] }))} className="p-1 rounded hover:bg-neutral-100" aria-label="Toggle brand" data-testid={`fabric-tree-toggle-${b.name}`}>
              {openBrands[b.name] ? <ChevronDown className="w-4 h-4 text-neutral-400" /> : <ChevronRight className="w-4 h-4 text-neutral-400" />}
            </button>
            <button onClick={() => { onScope({ brand: b.name }); setOpenBrands((p) => ({ ...p, [b.name]: true })); }} className={`flex-1 flex items-center gap-2 text-left py-0.5 ${isBrand(b.name) ? 'text-amber-800 font-semibold' : 'text-neutral-900 font-semibold'}`} data-testid={`fabric-tree-brand-${b.name}`}>
              {brandLabel(b.name)} <span className="ml-auto text-xs text-neutral-400 font-normal">{b.collections.length} {(b.collections.length === 1 ? tierLabels(b.name).tier2 : tierLabels(b.name).tier2Plural).toLowerCase()}</span>
            </button>
          </div>
          {openBrands[b.name] && (
            <div className="pb-1">
              {b.collections.map((c) => {
                const key = `${b.name}/${c.name}`;
                const hasMats = c.materials.length > 0;
                return (
                  <div key={key}>
                    <div className={`flex items-center gap-1 pl-6 pr-3 py-1.5 ${isColl(b.name, c.name) ? 'bg-amber-50' : 'hover:bg-neutral-50'}`}>
                      {hasMats ? (
                        <button onClick={() => setOpenColls((p) => ({ ...p, [key]: !p[key] }))} className="p-0.5 rounded hover:bg-neutral-100" aria-label="Toggle collection">
                          {openColls[key] ? <ChevronDown className="w-3.5 h-3.5 text-neutral-400" /> : <ChevronRight className="w-3.5 h-3.5 text-neutral-400" />}
                        </button>
                      ) : <span className="w-4.5 inline-block w-[18px]" />}
                      <button
                        onClick={() => { onScope({ brand: b.name, collection: c.name }); setOpenColls((p) => ({ ...p, [key]: true })); }}
                        className={`flex-1 flex items-center gap-2 text-left min-w-0 ${isColl(b.name, c.name) ? 'text-amber-800 font-medium' : 'text-neutral-700'}`}
                        data-testid={`fabric-tree-collection-${c.name}`}
                      >
                        {c.thumb ? <ColorImage src={c.thumb} alt="" w={128} eager className="w-5 h-5 rounded border border-neutral-200 overflow-hidden flex-shrink-0" /> : <FolderOpen className="w-4 h-4 text-neutral-400 flex-shrink-0" />}
                        <span className="truncate">{c.name}</span>
                        <span className="ml-auto text-[11px] text-neutral-400 flex-shrink-0">{c.count}</span>
                      </button>
                    </div>
                    {hasMats && openColls[key] && c.materials.map((m) => (
                      <button
                        key={m.name}
                        onClick={() => onScope({ brand: b.name, collection: c.name, material: m.name })}
                        className={`w-full flex items-center gap-2 pl-14 pr-3 py-1 text-left text-xs ${isMat(b.name, c.name, m.name) ? 'bg-amber-100/60 text-amber-900 font-medium' : 'text-neutral-600 hover:bg-neutral-50'}`}
                        data-testid={`fabric-tree-material-${c.name}-${m.name}`}
                      >
                        {m.thumb ? <ColorImage src={m.thumb} alt="" w={128} eager className="w-4 h-4 rounded border border-neutral-200 overflow-hidden flex-shrink-0" /> : <Palette className="w-3.5 h-3.5 text-neutral-400" />}
                        <span className="truncate">{m.name}</span>
                        <span className="ml-auto text-[10px] text-neutral-400">{m.count}</span>
                      </button>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ))}
    </nav>
  );
};

export default FabricTreeNav;
